'use client'

import { Phone, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import React, { useState, useEffect } from "react"
import Image from "next/image"
import Link from "next/link"
import { ArrowRightIcon } from "./ui/arrow-right-icon"


const NAVBAR_PHONE = "+1 888 830 7444"

interface NavbarProps {
  isDarkBackground?: boolean;
}

export const Navbar: React.FC<NavbarProps> = ({ isDarkBackground = true }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)


  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  // Close menu on Escape key press
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isMenuOpen]);

  const handleMenuClick = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const handleMenuClose = () => {
    setIsMenuOpen(false)
  }

  // Smooth close with slight delay for better UX
  const handleSmoothMenuClose = () => {
    setIsMenuOpen(false)
  }

  return (
    <>
      <header className="w-full px-2 pt-6 pb-2 flex items-center justify-between rounded-3xl max-w-[1280px] mx-auto relative z-50 gap-4">
        {/* Logo */}
        <Link href="/" className="flex items-center select-none cursor-pointer" tabIndex={0} aria-label="fls logo">
          <Image 
            src={isMenuOpen || !isDarkBackground ? "/logos/fls-teal.svg" : "/logos/fls.svg"}
            alt="FLS Logo" 
            width={90} 
            height={60} 
            className="w-[70px] h-[47px] sm:w-[90px] sm:h-[60px]"
            style={{height: 'auto'}}
            priority
          />
        </Link>
        {/* Right side */}
        <div className="flex items-center gap-4">

          {/* Phone CTA */}
          <Button
            variant="default"
            className="flex items-center gap-2 px-5 py-3 bg-[#D44A26] text-white font-bold text-[14px] font-poppins uppercase leading-none rounded-full focus:outline-none focus:ring-2 focus:ring-[#D44A26] cursor-pointer transition-all duration-200 hover:brightness-95"
            tabIndex={0}
            aria-label={`Call us at ${NAVBAR_PHONE}`}
            onClick={() => window.open(`tel:${NAVBAR_PHONE}`, '_self')}
          >
            <Phone size={16} />
            <span>{NAVBAR_PHONE}</span>
          </Button>
          {/* Menu button */}
          <button
            onClick={handleMenuClick}
            tabIndex={0}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-white rounded-full focus:outline-none focus:ring-2 focus:ring-[#0ABAB5] group cursor-pointer"
          >
            {isMenuOpen ? (
              <X size={20} className="text-[#0ABAB5]" />
            ) : (
              <div className="flex flex-col items-center justify-center gap-1">
                <span className="block h-0.5 w-5 bg-[#0ABAB5] rounded"></span>
                <span className="block h-0.5 w-5 bg-[#0ABAB5] rounded"></span>
                <span className="block h-0.5 w-5 bg-[#0ABAB5] rounded"></span>
              </div>
            )}
            <span className="font-bold text-[14px] font-poppins uppercase text-[#0ABAB5]">MENU</span>
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-white z-40 transition-all duration-500 ease-in-out ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
        onClick={(e) => {
          // Close menu if clicking on the overlay background
          if (e.target === e.currentTarget) {
            setIsMenuOpen(false);
          }
        }}
      >
        <div 
          className={`flex flex-col h-full transition-all duration-500 ease-in-out ${isMenuOpen ? 'translate-y-0' : '-translate-y-full'}`}
          onTouchStart={(e) => {
            const touch = e.touches[0];
            e.currentTarget.dataset.startY = touch.clientY.toString();
          }}
          onTouchEnd={(e) => {
            const startY = parseFloat(e.currentTarget.dataset.startY || '0');
            const endY = e.changedTouches[0].clientY;
            const deltaY = endY - startY;
            
            // Close menu if swiped down more than 100px
            if (deltaY > 100) {
              setIsMenuOpen(false);
            }
          }}
        >
          {/* Top spacing for navbar */}
          <div className="h-20 sm:h-24 flex-shrink-0"></div>
          
          {/* Divider */}
          <div className="max-w-[1280px] mx-auto w-full px-4 sm:px-8 flex-shrink-0">
            <div className="border-t border-[#E5E5E5]"></div>
          </div>
          
          {/* Scrollable Menu Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="flex flex-col lg:flex-row max-w-[1280px] mx-auto w-full min-h-full">
            {/* Left Side - Navigation */}
            <div className="flex-1 flex flex-col justify-start px-4 sm:px-8 py-6 sm:py-8 lg:border-r border-[#E5E5E5]">
                <nav className="space-y-4 sm:space-y-6">
                    {/* Main Navigation Items */}
                    <div className="space-y-2 sm:space-y-4">
                        <Link href="/#best-deal-of-the-day" onClick={handleMenuClose} className="group block cursor-pointer">
                            <div className="flex items-center justify-between py-2 border-b border-transparent group-hover:border-[#0ABAB5]/20 transition-all duration-300">
                                <span className="font-bold text-[24px] sm:text-[32px] lg:text-[40px] font-poppins uppercase text-[#2C3E50] leading-tight tracking-tight group-hover:text-[#0ABAB5] transition-all duration-300 transform group-hover:translate-x-2">
                                    BEST DEALS
                                </span>
                                <div className="ml-2 sm:ml-4 transition-all duration-300 group-hover:translate-x-2 group-hover:scale-110 text-[#2C3E50] group-hover:text-[#0ABAB5]">
                                    <ArrowRightIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                                </div>
                            </div>
                        </Link>
                        
                        <Link href="/all-airlines" onClick={handleMenuClose} className="group block cursor-pointer">
                            <div className="flex items-center justify-between py-2 border-b border-transparent group-hover:border-[#0ABAB5]/20 transition-all duration-300">
                                <span className="font-bold text-[24px] sm:text-[32px] lg:text-[40px] font-poppins uppercase text-[#2C3E50] leading-tight tracking-tight group-hover:text-[#0ABAB5] transition-all duration-300 transform group-hover:translate-x-2">
                                    TOP AIRLINES
                                </span>
                                <div className="ml-2 sm:ml-4 transition-all duration-300 group-hover:translate-x-2 group-hover:scale-110 text-[#2C3E50] group-hover:text-[#0ABAB5]">
                                    <ArrowRightIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                                </div>
                            </div>
                        </Link>
                        
                        <Link href="/top-cities" onClick={handleMenuClose} className="group block cursor-pointer">
                            <div className="flex items-center justify-between py-2 border-b border-transparent group-hover:border-[#0ABAB5]/20 transition-all duration-300">
                                <span className="font-bold text-[24px] sm:text-[32px] lg:text-[40px] font-poppins uppercase text-[#2C3E50] leading-tight tracking-tight group-hover:text-[#0ABAB5] transition-all duration-300 transform group-hover:translate-x-2">
                                    TOP CITIES
                                </span>
                                <div className="ml-2 sm:ml-4 transition-all duration-300 group-hover:translate-x-2 group-hover:scale-110 text-[#2C3E50] group-hover:text-[#0ABAB5]">
                                    <ArrowRightIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                                </div>
                            </div>
                        </Link>
                        
                        <Link href="/top-countries" onClick={handleMenuClose} className="group block cursor-pointer">
                            <div className="flex items-center justify-between py-2 border-b border-transparent group-hover:border-[#0ABAB5]/20 transition-all duration-300">
                                <span className="font-bold text-[24px] sm:text-[32px] lg:text-[40px] font-poppins uppercase text-[#2C3E50] leading-tight tracking-tight group-hover:text-[#0ABAB5] transition-all duration-300 transform group-hover:translate-x-2">
                                    TOP COUNTRIES
                                </span>
                                <div className="ml-2 sm:ml-4 transition-all duration-300 group-hover:translate-x-2 group-hover:scale-110 text-[#2C3E50] group-hover:text-[#0ABAB5]">
                                    <ArrowRightIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                                </div>
                            </div>
                        </Link>
                    </div>
                    
                    {/* Secondary Navigation */}
                    <div className="pt-4 space-y-2 border-t border-[#E5E5E5]/50">
                        <Link href="/contact" onClick={handleMenuClose} className="group block cursor-pointer">
                            <div className="flex items-center justify-between py-1">
                                <span className="font-semibold text-[16px] sm:text-[20px] font-poppins uppercase text-[#2C3E50] leading-tight tracking-tight group-hover:text-[#0ABAB5] transition-all duration-300">
                                    CONTACT
                                </span>
                                <div className="ml-2 transition-all duration-300 group-hover:translate-x-1 text-[#2C3E50] group-hover:text-[#0ABAB5]">
                                    <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                </div>
                            </div>
                        </Link>
                        
                        <Link href="/faq" onClick={handleMenuClose} className="group block cursor-pointer">
                            <div className="flex items-center justify-between py-1">
                                <span className="font-semibold text-[16px] sm:text-[20px] font-poppins uppercase text-[#2C3E50] leading-tight tracking-tight group-hover:text-[#0ABAB5] transition-all duration-300">
                                    FAQ
                                </span>
                                <div className="ml-2 transition-all duration-300 group-hover:translate-x-1 text-[#2C3E50] group-hover:text-[#0ABAB5]">
                                    <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                </div>
                            </div>
                        </Link>
                    </div>
                </nav>
            </div>

            {/* Right Side - Contact Info */}
            <div className="flex-1 flex flex-col justify-between items-start text-left px-4 sm:px-8 py-4 sm:py-6 border-t lg:border-t-0 lg:border-l border-[#E5E5E5]">
              <div className="space-y-4">
                {/* Phone */}
                <div className="space-y-1">
                  <div className="flex items-center justify-start gap-2">
                    <Phone size={16} className="sm:w-5 sm:h-5 text-[#EC5E39]" />
                    <span className="font-bold text-[12px] sm:text-[14px] font-poppins text-[#2C3E50]">Call us 24/7</span>
                  </div>
                  <a href="tel:+18888307444" className="block text-[20px] sm:text-[24px] font-normal font-poppins text-[#2C3E50] transition-all duration-300 cursor-pointer hover:[text-shadow:0_0_0.5px_#2C3E50] focus:outline-none focus:ring-2 focus:ring-[#0ABAB5] focus:ring-offset-2 rounded">
                    +1 (888) 830-7444
                  </a>
                </div>

                {/* Email */}
                <div className="space-y-1">
                  <div className="flex items-center justify-start gap-2">
                    <svg width="16" height="16" className="sm:w-5 sm:h-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3.33333 3.33334H16.6667C17.5833 3.33334 18.3333 4.08334 18.3333 5.00001V15C18.3333 15.9167 17.5833 16.6667 16.6667 16.6667H3.33333C2.41667 16.6667 1.66667 15.9167 1.66667 15V5.00001C1.66667 4.08334 2.41667 3.33334 3.33333 3.33334Z" stroke="#EC5E39" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M18.3333 5L10 10.8333L1.66667 5" stroke="#EC5E39" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="font-bold text-[12px] sm:text-[14px] font-poppins text-[#2C3E50]">E-mail</span>
                  </div>
                  <a href="mailto:care@flyluxsky.com" className="block text-[16px] sm:text-[18px] font-normal font-poppins text-[#2C3E50] transition-all duration-300 cursor-pointer hover:[text-shadow:0_0_0.5px_#2C3E50] focus:outline-none focus:ring-2 focus:ring-[#0ABAB5] focus:ring-offset-2 rounded">
                    care@flyluxsky.com
                  </a>
                </div>

                {/* Social Icons */}
                <div className="flex items-center justify-start gap-2 sm:gap-3 pt-2 flex-wrap">
                  <a href="https://www.instagram.com/luxeskies_com/" target="_blank" rel="noopener noreferrer" className="bg-[#F8F8F8] hover:bg-gray-200 p-2 rounded-lg transition-colors flex items-center justify-center cursor-pointer hover:scale-110 transition-transform duration-300 focus:outline-none focus:ring-2 focus:ring-[#0ABAB5] focus:ring-offset-2" aria-label="Instagram">
                    <Image src="/social/instagram.svg" alt="Instagram" width={20} height={20} />
                  </a>
                  <a href="https://www.facebook.com/luxeskies" target="_blank" rel="noopener noreferrer" className="bg-[#F8F8F8] hover:bg-gray-200 p-2 rounded-lg transition-colors flex items-center justify-center cursor-pointer hover:scale-110 transition-transform duration-300 focus:outline-none focus:ring-2 focus:ring-[#0ABAB5] focus:ring-offset-2" aria-label="Facebook">
                    <Image src="/social/facebook.svg" alt="Facebook" width={20} height={20} />
                  </a>
                  <a href="https://www.youtube.com/@LuxeSkies" target="_blank" rel="noopener noreferrer" className="bg-[#F8F8F8] hover:bg-gray-200 p-2 rounded-lg transition-colors flex items-center justify-center cursor-pointer hover:scale-110 transition-transform duration-300 focus:outline-none focus:ring-2 focus:ring-[#0ABAB5] focus:ring-offset-2" aria-label="YouTube">
                    <Image src="/social/youtube.svg" alt="YouTube" width={20} height={20} />
                  </a>
                  <a href="https://www.linkedin.com/company/luxeskiesinc/" target="_blank" rel="noopener noreferrer" className="bg-[#F8F8F8] hover:bg-gray-200 p-2 rounded-lg transition-colors flex items-center justify-center cursor-pointer hover:scale-110 transition-transform duration-300 focus:outline-none focus:ring-2 focus:ring-[#0ABAB5] focus:ring-offset-2" aria-label="LinkedIn">
                    <Image src="/social/linkedin.svg" alt="LinkedIn" width={20} height={20} />
                  </a>
                  <a href="https://www.tiktok.com/@luxeskies" target="_blank" rel="noopener noreferrer" className="bg-[#F8F8F8] hover:bg-gray-200 p-2 rounded-lg transition-colors flex items-center justify-center cursor-pointer hover:scale-110 transition-transform duration-300 focus:outline-none focus:ring-2 focus:ring-[#0ABAB5] focus:ring-offset-2" aria-label="TikTok">
                    <Image src="/social/tiktok.svg" alt="TikTok" width={20} height={20} />
                  </a>
                  <a href="https://www.trustpilot.com/review/luxeskies.com" target="_blank" rel="noopener noreferrer" className="bg-[#F8F8F8] hover:bg-gray-200 p-2 rounded-lg transition-colors flex items-center justify-center cursor-pointer hover:scale-110 transition-transform duration-300 focus:outline-none focus:ring-2 focus:ring-[#0ABAB5] focus:ring-offset-2" aria-label="Trustpilot">
                    <Image src="/social/trustpilot.svg" alt="Trustpilot" width={20} height={20} />
                  </a>
                </div>
              </div>
              <div>
                <div className="pt-4 sm:pt-6">
                  <div className="flex flex-wrap items-center justify-start gap-2 sm:gap-4">
                    <Image src="/icons/logo-white 1.svg" alt="Trustpilot" width={70} height={18} className="sm:w-[90px] sm:h-[23px]" style={{height: 'auto'}} />
                    <Image src="/icons/cdnlogo.com_discover 1.svg" alt="Discover" width={60} height={11} className="sm:w-[80px] sm:h-[14px]" style={{height: 'auto'}} />
                    <Image src="/icons/mas.svg" alt="Mastercard" width={30} height={18} className="sm:w-[40px] sm:h-[24px]" style={{height: 'auto'}} />
                    <Image src="/icons/American-Express-logo.svg" alt="American Express" width={55} height={19} className="sm:w-[70px] sm:h-[24px]" style={{height: 'auto'}} />
                    <Image src="/icons/Visa_2021.svg" alt="Visa" width={35} height={12} className="sm:w-[45px] sm:h-[15px]" style={{height: 'auto'}} />
                  </div>
                </div>
                <div className="mt-3 sm:mt-4">
                  <p className="text-[10px] sm:text-[12px] lg:text-[14px] text-[#7A7A7A] font-poppins">
                      © 2025 FlyLuxSky. All rights reserved.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        </div>
      </div>
    </>
  )
}

export default Navbar